import StageQr from "@/components/StageQr";
import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

// The Quality Check stage as its two paper forms (PF005/F02-00): page 10 "8. Batch Reconciliation"
// (what was issued, obtained and wasted at each step, then the final quantities) and page 11
// "9. Random Sampling" (sampling, AQL, analysis details, batch approval). Shown as two tabs,
// printed as two sheets. Quantities of the earlier steps come from the stage records already
// made (Printing, AVT, Slitting); anything typed here is kept as typed.
const PROCESSES = ["Flexo Printing", "AVT", "Slitting & Numbering", "Sorting/ Packing"];
const AQL = [["Critical", 0, "NIL"], ["Major", 2, "NMT 2%"], ["Minor", 4, "NMT 4%"]];
const TOTAL_AQL_LIMIT = 5;
const DEFECT_MIN = 3;
const has = (v) => v !== undefined && v !== null && v !== "";
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => (Math.round(n * 1000) / 1000).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const pct = (n) => `${(Math.round(n * 100) / 100).toFixed(2)}%`;
const arr = (v) => (Array.isArray(v) ? v : []);
const blankDefect = () => ({ type: "", description: "", defectives: "" });

// Fields of this stage the sheets cover (all of them: there are no extras to keep under "More details").
export const QUALITY_SHEET_FIELDS = new Set([
  "recon_rows", "recon_remarks", "order_qty", "obtained_qty", "packed_qty", "destruction_qty", "destruction_done_by",
  "destruction_checked_by", "sample_date", "total_boxes", "random_boxes", "samples_per_box", "total_samples", "defect_rows",
  "total_defects", "an_paper_type", "an_design", "an_gsm", "an_screens", "an_release_paper", "an_varnish_zone",
  "an_print_registration", "an_colour", "an_dimensions", "an_up_numbers", "an_text", "an_numbering_back", "an_winding",
  "an_packed_qty", "an_approved_qty", "sampling_remarks", "checked_by_qc", "approved_by_qc_head",
]);

/** The four process rows with what the earlier stages recorded as the starting figures (typed values win). */
export function reconRows(data, carried = {}) {
  const typed = arr(data.recon_rows);
  const start = [
    { issued: "", obtained: carried.qty_forward_to_avt_labels },
    { issued: carried.qty_received_from_printing, obtained: carried.qty_forward_to_slitting },
    { issued: carried.qty_received_from_avt, obtained: carried.qty_forward_to_packing },
    { issued: carried.qty_forward_to_packing, obtained: "" },
  ];
  return PROCESSES.map((name, i) => {
    const t = typed[i] || {};
    return {
      name,
      issued: has(t.issued) ? t.issued : start[i].issued ?? "",
      obtained: has(t.obtained) ? t.obtained : start[i].obtained ?? "",
      done_by: t.done_by ?? "",
      checked_by: t.checked_by ?? "",
    };
  });
}
const wastageOf = (r) => (has(r.issued) && has(r.obtained) ? num(r.issued) - num(r.obtained) : null);

/** Sampling figures: samples checked, defects found and the AQL percentages against the paper's limits. */
export function sampling(data) {
  const rows = arr(data.defect_rows);
  const boxes = num(data.random_boxes), per = num(data.samples_per_box);
  const samples = boxes > 0 && per > 0 ? boxes * per : num(data.total_samples);
  const defects = rows.reduce((s, r) => s + num(r.defectives), 0);
  const byType = Object.fromEntries(AQL.map(([t]) => [t, rows.filter((r) => r.type === t).reduce((s, r) => s + num(r.defectives), 0)]));
  const rate = (n) => (samples > 0 ? (n / samples) * 100 : null);
  return { samples, defects, byType, total: rate(defects), rates: Object.fromEntries(AQL.map(([t]) => [t, rate(byType[t])])) };
}

/** The stage fields to save for the current sampling: samples checked and defects found. */
export function qualityTotalsPatch(data) {
  const s = sampling(data);
  const patch = {};
  if (s.samples > 0) patch.total_samples = s.samples;
  if (arr(data.defect_rows).some((r) => has(r.defectives))) patch.total_defects = s.defects;
  return patch;
}

const line = "border border-neutral-700";
const cell = `${line} px-1.5 py-1 text-[11px] align-middle`;
const input = "w-full bg-transparent outline-none text-[11px] font-medium placeholder:text-neutral-300 focus:bg-orange-50 disabled:text-neutral-800";
const dotted = "border-b border-dotted border-neutral-600 pb-0.5";

/** `data` is the stage record, `ctx` its context (the job, what the earlier stages carried, the job card's winding), `setMany(patch)` changes keys. */
export default function QualitySheet({ ctx, data, setMany, readOnly }) {
  const [page, setPage] = useState(1);
  const common = ctx?.common || {};
  const carried = ctx?.carried || {};
  const rows = reconRows(data, carried);
  const wasted = rows.map(wastageOf);
  const totalWaste = wasted.reduce((s, w) => s + (w ?? 0), 0);
  const sample = sampling(data);
  const defects = [...arr(data.defect_rows), ...Array.from({ length: Math.max(DEFECT_MIN - arr(data.defect_rows).length, 0) }, blankDefect)];

  const change = (patch) => setMany({ ...patch, ...qualityTotalsPatch({ ...data, ...patch }) });
  const editRecon = (i, k, v) => {
    const next = rows.map((r) => ({ issued: r.issued, obtained: r.obtained, done_by: r.done_by, checked_by: r.checked_by }));
    next[i][k] = v;
    setMany({ recon_rows: next });
  };
  const editDefect = (i, k, v) => {
    const next = defects.map((r) => ({ ...r }));
    next[i][k] = v;
    change({ defect_rows: next.filter((r, at) => at < arr(data.defect_rows).length || Object.values(r).some(has)) });
  };
  const winding = Object.entries(data.winding_ticks || ctx?.winding || {}).filter(([, on]) => on).map(([k]) => k).join(", ");
  const dims = has(common.label_length_mm) && has(common.label_width_mm) ? `${common.label_length_mm} x ${common.label_width_mm} mm` : "";
  const shown = (k, fallback = "") => (has(data[k]) ? data[k] : fallback ?? "");

  // Render helpers, not components: a component defined here would remount on every keystroke.
  const text = (label, k, { fallback = "", width = "", type = "text", on } = {}) => (
    <div key={k} className={`flex items-end gap-1.5 ${width}`}>
      <span className="text-[11px] whitespace-nowrap">{label}</span>
      <input type={type} className={`${input} ${dotted}`} value={shown(k, fallback)} disabled={readOnly}
        onChange={(e) => (on ? on(e.target.value) : setMany({ [k]: e.target.value }))} data-testid={`qc-${k}`} />
    </div>
  );
  const cellText = (testid, value, onChange, { type = "text", right = false } = {}) => (
    <td className={cell}>
      <input type={type} className={`${input} ${right ? "text-right font-mono-num" : ""}`} value={value ?? ""} disabled={readOnly}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </td>
  );
  const letterhead = (title, pageNo) => (
    <div className="flex items-start justify-between px-3 pt-2 pb-1">
      <div>
        <div className="font-bold text-sm">MedPack India Pvt. Ltd.</div>
        <div className="print-only text-[10px] text-neutral-700">
          BPR {common.mpwo_number} · {[common.customer_name, common.product_name].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div className="font-semibold text-[12px] border-b border-neutral-700 px-6">{title}</div>
      <div className="flex items-start gap-2"><StageQr jobId={ctx.job_id} stage="quality" /><div className="text-[11px] text-neutral-600">Page {pageNo} of 11</div></div>
    </div>
  );
  const analysis = [
    [["Type of Paper :", "an_paper_type", carried.paper_type], ["Design :", "an_design"]],
    [["GSM :", "an_gsm", common.gsm], ["Screens/Gradients :", "an_screens"]],
    [["Release Paper :", "an_release_paper"], ["Varnish Zone :", "an_varnish_zone"]],
    [["Print Registration :", "an_print_registration"], ["Colour :", "an_colour"]],
    [["Dimensions :", "an_dimensions", dims], ["Up Numbers :", "an_up_numbers"]],
    [["Text :", "an_text"], ["Numbering on back side :", "an_numbering_back"]],
  ];

  return (
    <div className="space-y-2" data-testid="quality-sheet">
      <div className="no-print flex flex-wrap items-center gap-1 border border-neutral-200 bg-white p-1 rounded-sm w-fit" data-testid="quality-tabs">
        {[[1, "Page 10 of 11 · Batch reconciliation"], [2, "Page 11 of 11 · Random sampling"]].map(([n, label]) => (
          <button key={n} type="button" onClick={() => setPage(n)} data-testid={`quality-tab-${n}`}
            className={`px-3 py-1.5 text-xs rounded-sm font-medium transition-colors ${page === n ? "bg-[#FF5A00] text-white" : "text-neutral-600 hover:bg-neutral-100"}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ---- page 10 ---- */}
      <div className={`bg-white border border-neutral-700 text-neutral-900 ${page === 1 ? "" : "hidden"}`} data-testid="quality-page-1">
        {letterhead("8. BATCH RECONCILIATION", "10")}
        <div className="px-3 pb-2 space-y-2">
          <div className="text-center text-[10px]">(Records of Reconciliation at All Steps of Production)</div>
          <table className="w-full border-collapse" data-testid="qc-recon">
            <thead>
              <tr className="align-top">
                <th rowSpan={2} className={`${cell} text-left font-semibold`}>S. No.</th>
                <th rowSpan={2} className={`${cell} text-left font-semibold`}>Process</th>
                <th rowSpan={2} className={`${cell} text-left font-semibold leading-tight`}>Issued<br />Qty</th>
                <th rowSpan={2} className={`${cell} text-left font-semibold leading-tight`}>Obtained<br />Qty</th>
                <th rowSpan={2} className={`${cell} text-left font-semibold leading-tight`}>Process<br />Wastage</th>
                <th colSpan={2} className={`${cell} text-center font-semibold`}>Wastage<br />Destruction</th>
              </tr>
              <tr>
                <th className={`${cell} text-left font-medium leading-tight`}>Done by<br />(Production)</th>
                <th className={`${cell} text-left font-medium leading-tight`}>Checked by<br />(Q.C.)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.name} className="h-9">
                  <td className={`${cell} text-center`}>{i + 1}.</td>
                  <td className={cell}>{r.name}</td>
                  {cellText(`qc-recon-${i}-issued`, r.issued, (v) => editRecon(i, "issued", v), { type: "number", right: true })}
                  {cellText(`qc-recon-${i}-obtained`, r.obtained, (v) => editRecon(i, "obtained", v), { type: "number", right: true })}
                  <td className={`${cell} text-right font-mono-num`} data-testid={`qc-recon-${i}-wastage`}>
                    {wasted[i] !== null && <>{fmt(wasted[i])} <span className="font-sans text-neutral-600">(Labels)</span></>}
                  </td>
                  {cellText(`qc-recon-${i}-done_by`, r.done_by, (v) => editRecon(i, "done_by", v))}
                  {cellText(`qc-recon-${i}-checked_by`, r.checked_by, (v) => editRecon(i, "checked_by", v))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`${line} p-1.5`}>
            <div className="text-[11px] font-bold">Remarks :</div>
            <textarea className={`${input} min-h-24 resize-none`} value={data.recon_remarks ?? ""} disabled={readOnly}
              onChange={(e) => setMany({ recon_remarks: e.target.value })} data-testid="qc-recon_remarks" />
          </div>

          <div className="font-bold text-[12px] underline pt-1">FINAL</div>
          <div className="space-y-2 max-w-md">
            {text("Order Quantity :", "order_qty", { fallback: common.quantity })}
            {text("Obtained Quantity :", "obtained_qty", { fallback: rows[2].obtained })}
            {text("Packed Quantity :", "packed_qty")}
            <div className="pt-2">{text("Destruction Quantity :", "destruction_qty", { fallback: totalWaste > 0 ? totalWaste : "" })}</div>
            {text("Destruction Done by & Date : (Production)", "destruction_done_by")}
            {text("Checked by & Date : (Q.C.)", "destruction_checked_by")}
          </div>
        </div>
        <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
      </div>

      {/* ---- page 11 ---- */}
      <div className={`bg-white border border-neutral-700 text-neutral-900 ${page === 2 ? "" : "hidden"}`} data-testid="quality-page-2">
        {letterhead("9. RANDOM SAMPLING", "11")}
        <div className="px-3 pb-2 space-y-2">
          <div className="flex items-end justify-between gap-6">
            <div className="max-w-xs flex-1">{text("Total Boxes", "total_boxes")}</div>
            <div className="max-w-xs flex-1">{text("Date :", "sample_date", { type: "date" })}</div>
          </div>
          <table className="w-full border-collapse" data-testid="qc-sampling">
            <thead>
              <tr className="align-top">
                <th className={`${cell} text-left font-semibold leading-tight`}>No. of Random<br />boxes selection</th>
                <th className={`${cell} text-left font-semibold leading-tight`}>Samples taken<br />per box</th>
                <th className={`${cell} text-left font-semibold leading-tight`}>Total<br />Samples<br />Checked</th>
                <th className={`${cell} text-left font-semibold leading-tight`}>Total<br />Defects</th>
                <th colSpan={2} className={`${cell} text-left font-semibold leading-tight`}>
                  Total AQL<br />(Not more than {TOTAL_AQL_LIMIT}%)
                  {sample.total !== null && (
                    <div className={`font-mono-num ${sample.total > TOTAL_AQL_LIMIT ? "text-rose-700" : "text-emerald-700"}`} data-testid="qc-aql-total">found {pct(sample.total)}</div>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="h-8">
                {cellText("qc-random_boxes", data.random_boxes, (v) => change({ random_boxes: v }), { type: "number", right: true })}
                {cellText("qc-samples_per_box", data.samples_per_box, (v) => change({ samples_per_box: v }), { type: "number", right: true })}
                {boxesAndPer(data) ? (
                  <td className={`${cell} text-right font-mono-num`} data-testid="qc-total-samples">{fmt(sample.samples)}</td>
                ) : cellText("qc-total_samples", data.total_samples, (v) => change({ total_samples: v }), { type: "number", right: true })}
                <td className={`${cell} text-right font-mono-num`} data-testid="qc-total-defects">{arr(data.defect_rows).some((r) => has(r.defectives)) ? fmt(sample.defects) : ""}</td>
                <td className={`${cell} w-20`}>{AQL[0][0]}</td>
                <td className={`${cell} w-28 font-semibold`}>
                  {AQL[0][2]}
                  {sample.rates.Critical !== null && <div className={`font-mono-num font-normal ${sample.rates.Critical > 0 ? "text-rose-700" : "text-emerald-700"}`} data-testid="qc-aql-critical">found {pct(sample.rates.Critical)}</div>}
                </td>
              </tr>
              {AQL.slice(1).map(([t, limit, label]) => (
                <tr key={t}>
                  {t === "Major" && <td className={cell} colSpan={4} rowSpan={2} />}
                  <td className={`${cell} w-20`}>{t}</td>
                  <td className={`${cell} w-28 font-semibold`}>
                    {label}
                    {sample.rates[t] !== null && <div className={`font-mono-num font-normal ${sample.rates[t] > limit ? "text-rose-700" : "text-emerald-700"}`} data-testid={`qc-aql-${t.toLowerCase()}`}>found {pct(sample.rates[t])}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="w-full border-collapse" data-testid="qc-defects">
            <thead>
              <tr>
                <th className={`${cell} text-left font-semibold w-28`}>AQL type :</th>
                <th className={`${cell} text-center font-semibold`}>Description of defects</th>
                <th className={`${cell} text-left font-semibold w-32`}>No. of defectives</th>
              </tr>
            </thead>
            <tbody>
              {defects.map((r, i) => (
                <tr key={i} className="h-7">
                  <td className={cell}>
                    <select className={input} value={r.type ?? ""} disabled={readOnly} onChange={(e) => editDefect(i, "type", e.target.value)} data-testid={`qc-defect-${i}-type`}>
                      <option value=""></option>
                      {AQL.map(([t]) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  {cellText(`qc-defect-${i}-description`, r.description, (v) => editDefect(i, "description", v))}
                  {cellText(`qc-defect-${i}-defectives`, r.defectives, (v) => editDefect(i, "defectives", v), { type: "number", right: true })}
                </tr>
              ))}
            </tbody>
          </table>
          {!readOnly && (
            <Button type="button" variant="outline" size="sm" className="no-print rounded-sm h-6 gap-1 text-[11px]"
              onClick={() => change({ defect_rows: [...defects, blankDefect()] })} data-testid="qc-add-defect">
              <Plus size={11} /> Add row
            </Button>
          )}

          <div className="text-center font-bold text-[12px] underline pt-1">ANALYSIS DETAILS</div>
          <table className="w-full border-collapse" data-testid="qc-analysis">
            <tbody>
              {analysis.map((pair, i) => (
                <tr key={i} className="h-7">
                  {pair.map(([label, k, fallback]) => (
                    <FieldPair key={k} label={label} cellClass={cell}>
                      <input className={input} value={shown(k, fallback)} disabled={readOnly} onChange={(e) => setMany({ [k]: e.target.value })} data-testid={`qc-${k}`} />
                    </FieldPair>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className={`${line} px-1.5 py-1`}>{text("Winding Direction :", "an_winding", { fallback: winding })}</div>
          <div className={`${line} px-1.5 py-1 grid grid-cols-2 gap-x-6`}>
            {text("Packed Qty :", "an_packed_qty")}
            {text("Approved Qty :", "an_approved_qty")}
          </div>
          <div className={`${line} p-1.5`}>
            <div className="text-[11px] font-bold">REMARKS :</div>
            <textarea className={`${input} min-h-10 resize-none`} value={data.sampling_remarks ?? ""} disabled={readOnly}
              onChange={(e) => setMany({ sampling_remarks: e.target.value })} data-testid="qc-sampling_remarks" />
          </div>
          <div className={`${line} px-2 py-1.5 text-[12px] font-semibold`}>Batch is approved and can be dispatched to customer.</div>
          <div className={`${line} grid grid-cols-2`}>
            <div className="p-2 pt-6 border-r border-neutral-700">{text("Checked by & Date (Q.C.)", "checked_by_qc")}</div>
            <div className="p-2 pt-6">{text("Approved by & Date (Q.C. Head)", "approved_by_qc_head")}</div>
          </div>
        </div>
        <div className="px-3 pb-2 text-[10px] text-neutral-600">FORMAT No. PF005/F02-00</div>
      </div>
    </div>
  );
}

const boxesAndPer = (data) => num(data.random_boxes) > 0 && num(data.samples_per_box) > 0;

// One "label | value" pair of the analysis grid.
function FieldPair({ label, cellClass, children }) {
  return (
    <>
      <td className={`${cellClass} w-1/6 whitespace-nowrap`}>{label}</td>
      <td className={`${cellClass} w-1/3`}>{children}</td>
    </>
  );
}
